import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const slug = (name) => name.toLowerCase().replace(/\s+/g, '-');

const isRoute = (href) => href && href !== '#' && !href.startsWith('#');

const DesktopNav = ({
  navLinks,
  openDropdown,
  setOpenDropdown,
  onOpenReferralForm
}) => {
  const { pathname } = useLocation();

  const isLinkActive = (href) => isRoute(href) && pathname === href;
  const isParentActive = (link) =>
    isLinkActive(link.href) ||
    (link.dropdownItems || []).some((item) => !item.skipParentHighlight && isLinkActive(item.href));

  return (
    <div className="hidden 2xl:flex items-center space-x-5">
      {navLinks.map((link) => {
        const active = link.hasDropdown ? isParentActive(link) : isLinkActive(link.href);
        return link.hasDropdown ? (
          <div
            key={link.name}
            className="relative group flex-shrink-0"
            onMouseEnter={() => setOpenDropdown(link.dropdownKey)}
            onMouseLeave={() => setOpenDropdown(null)}
            onFocus={() => setOpenDropdown(link.dropdownKey)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setOpenDropdown(null);
            }}
          >
            <div className="flex items-center gap-1 py-2 whitespace-nowrap">
              {link.href && link.href !== '#' ? (
                <Link
                  to={link.href}
                  className={`transition-colors whitespace-nowrap ${active ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}
                  data-testid={`nav-${link.dropdownKey}-parent`}
                  aria-current={active ? 'page' : undefined}
                >
                  {link.name}
                </Link>
              ) : (
                <span
                  className={`transition-colors cursor-default whitespace-nowrap ${active ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}
                  data-testid={`nav-${link.dropdownKey}-parent`}
                  aria-haspopup="true"
                  aria-expanded={openDropdown === link.dropdownKey}
                  aria-current={active ? 'page' : undefined}
                >
                  {link.name}
                </span>
              )}
              <ChevronDown className={`w-4 h-4 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-700'}`} />
            </div>

            {openDropdown === link.dropdownKey && (
              <div className="absolute top-full left-0 pt-2 -mt-2 z-50">
                <div className="bg-white shadow-lg rounded-lg py-2 w-80 border border-gray-100">
                  {link.dropdownItems.map((item) => {
                    const itemActive = isLinkActive(item.href);
                    const itemClasses = `block px-4 py-3 transition-colors ${
                      itemActive ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }`;
                    return item.action === 'referralForm' ? (
                      <button
                        key={item.name}
                        onClick={onOpenReferralForm}
                        className={`w-full text-left ${itemClasses}`}
                        data-testid="nav-submit-referral-btn"
                      >
                        {item.name}
                      </button>
                    ) : item.href.startsWith('#') ? (
                      <a
                        key={item.name}
                        href={item.href}
                        className={itemClasses}
                        data-testid={`nav-item-${slug(item.name)}`}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={itemClasses}
                        data-testid={`nav-item-${slug(item.name)}`}
                        aria-current={itemActive ? 'page' : undefined}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : link.href.startsWith('/#') ? (
          <a
            key={link.name}
            href={link.href}
            className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap flex-shrink-0"
            data-testid={`nav-link-${slug(link.name)}`}
          >
            {link.name}
          </a>
        ) : (
          <Link
            key={link.name}
            to={link.href}
            className={`transition-colors whitespace-nowrap flex-shrink-0 ${active ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}
            data-testid={`nav-link-${slug(link.name)}`}
            aria-current={active ? 'page' : undefined}
          >
            {link.name}
          </Link>
        );
      })}
    </div>
  );
};

export default DesktopNav;
